import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, GitCommitVertical, ShieldCheck, Plus, Save, X, RotateCcw } from "lucide-react";
import { inventory } from "@/data/mockData";
import StatusPill from "@/components/aieco/StatusPill";
import log from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function diff(prev, draft) {
  const out = [];
  prev.forEach((line) => {
    if (!draft.includes(line)) out.push({ type: "remove", line });
    else out.push({ type: "context", line });
  });
  draft.forEach((line) => {
    if (!prev.includes(line)) out.push({ type: "add", line });
  });
  return out;
}

export default function PolicyEditor() {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const agent = inventory.find((a) => a.id === agentId);
  const [policy, setPolicy] = useState(null);
  const [draftRules, setDraftRules] = useState([]);
  const [newRule, setNewRule] = useState("");
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    if (!agent) return;
    axios.get(`${API}/policies/${agent.id}`).then((r) => {
      setPolicy(r.data);
      setDraftRules(r.data.draft_rules || []);
    }).catch((e) => {
      log.warn("[policy] fetch failed:", e?.message || e);
    });
  }, [agent]);

  if (!agent || !policy) {
    return (
      <div className="mx-auto max-w-[1480px] px-10 py-12 text-slate-400" data-testid={`page-policy-editor-${agentId}`}>
        <div className="text-sm">Loading policy…</div>
      </div>
    );
  }

  const draftVersion = policy.versions.find((v) => v.state === "draft");
  const activeVersion = policy.versions.find((v) => v.state === "active");
  const previousRules = activeVersion?.rules || [];
  const d = diff(previousRules, draftRules);
  const adds = d.filter((x) => x.type === "add").length;
  const removes = d.filter((x) => x.type === "remove").length;

  const persistDraft = async (rules) => {
    setDraftRules(rules);
    try {
      await axios.put(`${API}/policies/${agent.id}/draft`, { rules });
    } catch (e) {
      log.warn("[policy] draft persist failed:", e?.message || e);
    }
  };

  const addRule = () => {
    const v = newRule.trim();
    if (!v) return;
    const next = [...draftRules, v];
    persistDraft(next);
    setNewRule("");
  };

  const removeRule = (line) => {
    persistDraft(draftRules.filter((r) => r !== line));
  };

  const savePolicy = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/policies/${agent.id}/save`);
      setBanner(`Deployed ${r.data.active_version} to runtime.`);
      const fresh = await axios.get(`${API}/policies/${agent.id}`);
      setPolicy(fresh.data);
      setDraftRules(fresh.data.draft_rules || []);
    } finally {
      setBusy(false);
      setTimeout(() => setBanner(null), 4000);
    }
  };

  const rollbackTo = async (ver) => {
    setBusy(true);
    try {
      await axios.post(`${API}/policies/${agent.id}/rollback`, { target_version: ver });
      setBanner(`Rolled back active policy to ${ver}. Runtime enforcement updated.`);
      const fresh = await axios.get(`${API}/policies/${agent.id}`);
      setPolicy(fresh.data);
      setDraftRules(fresh.data.draft_rules || []);
    } finally {
      setBusy(false);
      setTimeout(() => setBanner(null), 4000);
    }
  };

  return (
    <div className="mx-auto max-w-[1480px] px-6 py-8 lg:px-10 lg:py-10" data-testid={`page-policy-editor-${agent.id}`}>
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <button onClick={() => navigate(`/inventory/${agent.id}`)} data-testid="back-to-agent" className="inline-flex items-center gap-1 text-[12px] text-slate-400 hover:text-slate-200">
          <ArrowLeft size={13} /> {agent.name}
        </button>

        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">Policy Editor</div>
            <h1 className="mt-2 font-display text-3xl font-medium tracking-tight text-white">
              {agent.name} · {activeVersion?.version} → {draftVersion?.version}
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Side-by-side diff of policy rules. Drafts and rollbacks persist server-side and deploy atomically.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill tone="info" dot={false}>
              <span className="font-mono">+{adds}</span> <span className="text-emerald-300">add</span> · <span className="font-mono">-{removes}</span> <span className="text-red-300">remove</span>
            </StatusPill>
            <button
              onClick={savePolicy}
              disabled={busy}
              data-testid="save-policy"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#5B8DEF] px-3 py-1.5 text-[12px] font-medium text-[#0b1020] hover:bg-[#7AA4F5] disabled:opacity-50"
            >
              <Save size={12} /> {busy ? "Deploying…" : "Save & deploy"}
            </button>
          </div>
        </div>
      </motion.div>

      <div className="mt-7 grid grid-cols-12 gap-5">
        <div className="card-eco col-span-12 overflow-hidden lg:col-span-7">
          <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Diff</div>
            <div className="flex items-center gap-3 font-mono text-[11px] text-slate-500">
              <span><span className="text-red-400">−</span> {activeVersion?.version}</span>
              <span><span className="text-emerald-400">+</span> {draftVersion?.version}</span>
            </div>
          </div>
          <div className="font-mono text-[12px]">
            {d.map((row, i) => {
              const color =
                row.type === "add"
                  ? { bg: "rgba(34,197,94,0.08)", fg: "#86efac", sign: "+", border: "border-l-emerald-500/60" }
                  : row.type === "remove"
                    ? { bg: "rgba(239,68,68,0.06)", fg: "#fca5a5", sign: "−", border: "border-l-red-500/60", strike: true }
                    : { bg: "transparent", fg: "#94a3b8", sign: " ", border: "border-l-transparent" };
              return (
                <div
                  key={i}
                  data-testid={`diff-row-${row.type}-${i}`}
                  className={`flex border-l-2 ${color.border} px-4 py-2`}
                  style={{ background: color.bg }}
                >
                  <span className="mr-3 w-3 text-slate-500">{color.sign}</span>
                  <span className={color.strike ? "line-through" : ""} style={{ color: color.fg }}>
                    {row.line}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-eco col-span-12 p-5 lg:col-span-5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Draft rules · {draftVersion?.version}</div>
            <span className="font-mono text-[10px] text-slate-500">{draftVersion?.date}</span>
          </div>

          <ul className="mt-3 space-y-1.5">
            {draftRules.map((r) => (
              <li key={r} className="group flex items-start justify-between gap-2 rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-[12px]">
                <span className="flex gap-2 text-slate-200">
                  <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#5B8DEF]" />
                  {r}
                </span>
                <button onClick={() => removeRule(r)} data-testid={`remove-rule-${r.slice(0, 12)}`} className="text-slate-500 opacity-0 hover:text-red-300 group-hover:opacity-100">
                  <X size={12} />
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-3 flex items-center gap-2">
            <input
              value={newRule}
              onChange={(e) => setNewRule(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addRule()}
              placeholder="Add a new rule…"
              data-testid="new-rule-input"
              className="flex-1 rounded-lg border border-white/5 bg-[#0b1020]/60 px-3 py-2 text-[12px] text-slate-200 placeholder:text-slate-500 focus:border-[#5B8DEF]/40 focus:outline-none"
            />
            <button onClick={addRule} data-testid="add-rule" className="inline-flex items-center gap-1 rounded-lg border border-white/5 bg-white/[0.02] px-2.5 py-2 text-[12px] text-slate-200 hover:border-white/10">
              <Plus size={12} /> Add
            </button>
          </div>

          <div className="mt-5 border-t border-white/5 pt-4">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Version history</div>
              <span className="font-mono text-[11px] text-slate-500">{policy.versions.length} versions · persisted</span>
            </div>

            {banner && (
              <div className="mt-2 rounded-lg border border-emerald-500/30 bg-emerald-500/[0.08] px-3 py-2 text-[12px] text-emerald-300" data-testid="rollback-banner">
                {banner}
              </div>
            )}

            <ol className="relative mt-3 space-y-2 border-l border-white/5 pl-4 text-[12px]" data-testid="version-timeline">
              {policy.versions.map((h) => {
                const isActive = h.state === "active";
                const tone = h.state === "draft" ? "info" : isActive ? "success" : "neutral";
                return (
                  <li
                    key={h.version}
                    className="relative rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2"
                    data-testid={`version-${h.version}`}
                  >
                    <span
                      className="absolute -left-[20px] top-3 h-2.5 w-2.5 rounded-full border-2 border-[#0B1020]"
                      style={{
                        background:
                          h.state === "draft" ? "#5B8DEF" : isActive ? "#22C55E" : "#475569",
                      }}
                    />
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <GitCommitVertical size={12} className="text-slate-500" />
                          <span className="text-slate-200">{h.version}</span>
                          <StatusPill tone={tone} dot={false}>
                            {h.state}
                          </StatusPill>
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{h.date} · {h.author}</div>
                        <div className="mt-0.5 text-[11px] text-slate-400">{h.note}</div>
                      </div>
                      {h.state === "archived" && (
                        <button
                          onClick={() => rollbackTo(h.version)}
                          disabled={busy}
                          data-testid={`rollback-${h.version}`}
                          className="inline-flex items-center gap-1 rounded-md border border-white/10 bg-white/[0.03] px-2 py-1 text-[11px] text-slate-200 hover:border-[#5B8DEF]/40 hover:text-white disabled:opacity-50"
                        >
                          <RotateCcw size={11} /> Rollback
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
